import React, { useCallback } from "react"
import { type FieldError, useFormContext, type RegisterOptions } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { type FormInputs } from "./form_input"
import Textarea, { type TextareaProps } from "./textarea"
import { dig } from "@/common/util/misc"

export interface FormTextareaProps extends TextareaProps {
  name: string
  rules?: RegisterOptions
  errorInterpolation?: object
}

const ForwardableFormTextarea: React.ForwardRefRenderFunction<HTMLTextAreaElement, FormTextareaProps> = (
  { rules, name, errorInterpolation, ...props },
  ref
) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormInputs>()
  const error = dig(errors, name) as FieldError | undefined
  const registerFields = register(name, rules)

  const { t } = useTranslation()
  const tOptions: Record<string, string> = { ...errorInterpolation }
  const errorMessage = error && (error.message ? t(error.message, tOptions) : t("misc.required_field"))

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      registerFields.onChange(e)

      if (props.onChange) props.onChange(e)
    },
    [props, registerFields]
  )

  return (
    <Textarea
      {...props}
      registerFields={registerFields}
      name={name}
      onChange={onChange}
      error={errorMessage}
      ref={ref}
    />
  )
}

const FormTextArea = React.forwardRef(ForwardableFormTextarea)

export default FormTextArea
